"use client";

import { DemoLink } from "@/components/demo-modal";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

export type PromoPopupProps = {
  /** ISO date with timezone, e.g. "2025-10-15T00:00:00+05:30". */
  startsAt: string;
  /** ISO date with timezone. The promo disappears completely after this. */
  endsAt: string;
  image: string;
  imageAlt: string;
  /** Short text on the minimized pill, e.g. "Diwali Offer". */
  pillLabel: string;
  /** Link for the CTA; if omitted the CTA opens the demo form. */
  ctaHref?: string;
  ctaLabel?: string;
  /** Delay before the card first appears. */
  delayMs?: number;
};

const SPARKS = [
  { left: "8%", top: "12%", delay: 0, size: 6 },
  { left: "88%", top: "20%", delay: 0.6, size: 8 },
  { left: "18%", top: "78%", delay: 1.1, size: 5 },
  { left: "92%", top: "70%", delay: 0.3, size: 7 },
  { left: "50%", top: "4%", delay: 0.9, size: 5 },
];

type Phase = "hidden" | "card" | "pill";

export function PromoPopup({
  startsAt,
  endsAt,
  image,
  imageAlt,
  pillLabel,
  ctaHref,
  ctaLabel = "Claim this offer",
  delayMs = 4000,
}: PromoPopupProps) {
  const [phase, setPhase] = useState<Phase>("hidden");

  // Date checks run on the client only to avoid hydration mismatch.
  useEffect(() => {
    const start = Date.parse(startsAt);
    const end = Date.parse(endsAt);
    const now = Date.now();

    if (Number.isNaN(start) || Number.isNaN(end) || now < start || now >= end) {
      return;
    }

    const showTimer = window.setTimeout(() => setPhase("card"), delayMs);
    // Hide automatically if the window closes while the page is open.
    const endTimer = window.setTimeout(
      () => setPhase("hidden"),
      Math.min(end - now, 2 ** 31 - 1),
    );

    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(endTimer);
    };
  }, [startsAt, endsAt, delayMs]);

  useEffect(() => {
    if (phase !== "card") {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        minimize();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [phase]);

  function minimize() {
    setPhase("pill");
  }

  const ctaClass =
    "mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#ffb020] to-[#ff6a00] px-5 py-3 text-sm font-semibold text-[#12033a] shadow-[0_8px_24px_rgba(255,140,0,0.45)] transition hover:brightness-110";

  return (
    <AnimatePresence>
      {phase === "card" && (
        <motion.aside
          key="card"
          aria-label={imageAlt}
          className="fixed right-4 bottom-4 z-[900] w-[min(340px,calc(100vw-2rem))] sm:right-6 sm:bottom-6"
          exit={{ opacity: 0, scale: 0.6, y: 40, rotate: 4 }}
          initial={{ opacity: 0, y: 80, rotate: -6, scale: 0.85 }}
          animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 220, damping: 20 }}
        >
          <div className="relative rounded-[28px] bg-gradient-to-br from-[#ffb020] via-[#ff4d8d] to-[#7a3cff] p-[2px] shadow-[0_24px_60px_rgba(18,3,58,0.55)]">
            <div className="relative overflow-hidden rounded-[26px] bg-[#12033a] p-3">
              <button
                aria-label="Close offer"
                className="absolute top-5 right-5 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur transition hover:bg-black/75"
                onClick={minimize}
                type="button"
              >
                <svg
                  aria-hidden="true"
                  fill="none"
                  height="14"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                  width="14"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>

              <div className="relative overflow-hidden rounded-2xl">
                <Image
                  alt={imageAlt}
                  className="h-auto w-full"
                  height={960}
                  priority
                  src={image}
                  width={768}
                />
                <motion.div
                  aria-hidden="true"
                  className="-left-1/2 -skew-x-12 pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{ x: ["0%", "500%"] }}
                  transition={{
                    duration: 1.6,
                    repeat: Number.POSITIVE_INFINITY,
                    repeatDelay: 3.5,
                    ease: "easeInOut",
                  }}
                />
              </div>

              {SPARKS.map((spark) => (
                <motion.span
                  aria-hidden="true"
                  className="pointer-events-none absolute rounded-full bg-[#ffd36a]"
                  key={spark.left + spark.top}
                  style={{
                    left: spark.left,
                    top: spark.top,
                    width: spark.size,
                    height: spark.size,
                  }}
                  animate={{ opacity: [0, 1, 0], scale: [0.5, 1.4, 0.5] }}
                  transition={{
                    duration: 2.4,
                    repeat: Number.POSITIVE_INFINITY,
                    delay: spark.delay,
                  }}
                />
              ))}

              {ctaHref ? (
                <Link className={ctaClass} href={ctaHref} onClick={minimize}>
                  {ctaLabel} <span aria-hidden="true">→</span>
                </Link>
              ) : (
                <DemoLink className={ctaClass} onClick={minimize}>
                  {ctaLabel} <span aria-hidden="true">→</span>
                </DemoLink>
              )}
            </div>
          </div>
        </motion.aside>
      )}

      {phase === "pill" && (
        <motion.button
          key="pill"
          aria-label={`Show ${pillLabel}`}
          className="fixed right-4 bottom-4 z-[900] flex items-center gap-2 rounded-full bg-gradient-to-r from-[#ffb020] via-[#ff4d8d] to-[#7a3cff] px-4 py-2.5 font-semibold text-sm text-white shadow-[0_10px_30px_rgba(18,3,58,0.45)] sm:right-6 sm:bottom-6"
          exit={{ opacity: 0, scale: 0.6 }}
          initial={{ opacity: 0, scale: 0.6, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          onClick={() => setPhase("card")}
          type="button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.96 }}
        >
          <motion.span
            aria-hidden="true"
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{
              duration: 1.2,
              repeat: Number.POSITIVE_INFINITY,
              repeatDelay: 2.5,
            }}
          >
            🪔
          </motion.span>
          {pillLabel}
        </motion.button>
      )}
    </AnimatePresence>
  );
}
